import Controller from '@ember/controller';
import { debounce, next } from '@ember/runloop';
import { action, computed } from '@ember/object';
import { readOnly, alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import {
  PROJECTS_COMPANY_TEAM_FILTER_OPTIONS,
  PROJECTS_NAV_TITLES,
  PROJECTS_NAV_TITLES_TO_ROUTES_MAP,
  PROJECTS_NETWORK_FILTER_API_ENUMS,
  PROJECTS_NETWORK_FILTER_OPTIONS,
  PROJECTS_SEARCH_CHARACTERS_BEFORE_REQUEST,
  PROJECTS_SORT_API_ENUMS,
  PROJECTS_SORT_OPTIONS,
} from 'sh-www/constants/routes/projects';
import { APPLICATION } from 'sh-www/models/application';
import { ICON_SORTS } from 'sh-www/constants/lsg';

class ProjectsController extends Controller {
  /**
   * Register services
   */
  @service router;
  @service session;
  @service('states/routes/projects') state;
  @service('state-managers/routes/projects') stateManager;

  /**
   * Reset this controller and state manager.
   */
  resetController() {
    this.stateManager.toggleIsStateResetting(true);
    this.set('_sortSelectedValue', PROJECTS_SORT_API_ENUMS.LAST_ACTIVITY);
    this.set('_companyTeamFilterSelectedValue', null);
    this.stateManager.resetStateManager(true);
  }

  /**
   * The array of application summaries we're showing for the view.
   *
   * @type {Ember.MutableArray}
   * @private
   */
  @readOnly('state.applicationListItems') _applicationListItems;

  /**
   * Counts for the inProgress application status.  This will keep track of the old count, so
   * the counts don't disappear on the navigation b/t page loads.
   *
   * @type {Number}
   * @private
   */
  @computed(
    '_previousInProgressCount',
    'state.applicationDashboardList.inProgressBuyerCount',
  )
  get _inProgressCount() {
    const previousCount = this._previousInProgressCount;
    const inProgressCount = this.state.applicationDashboardList?.inProgressBuyerCount;

    if (inProgressCount != null) {
      this.set('_previousInProgressCount', inProgressCount);
      return inProgressCount;
    }

    return previousCount;
  }

  /**
   * Counts for the completed application status.  This will keep track of the old count, so
   * the counts don't disappear on the navigation b/t page loads.
   *
   * @type {Number}
   * @private
   */
  @computed(
    '_previousCompletedCount',
    'state.applicationDashboardList.completedCount',
  )
  get _completedCount() {
    const previousCompletedCount = this._previousCompletedCount;
    const completedCount = this.state.applicationDashboardList?.completedCount;

    if (completedCount != null) {
      this.set('_previousCompletedCount', completedCount);
      return completedCount;
    }

    return previousCompletedCount;
  }

  /**
   * Counts for the cancelled application status.  This will keep track of the old count, so
   * the counts don't disappear on the navigation b/t page loads.
   *
   * @type {Number}
   * @private
   */
  @computed(
    '_previousCancelledCount',
    'state.applicationDashboardList.cancelledBuyerCount',
  )
  get _cancelledCount() {
    const previousCancelledCount = this._previousCancelledCount;
    const cancelledCount = this.state.applicationDashboardList?.cancelledBuyerCount;

    if (cancelledCount != null) {
      this.set('_previousCancelledCount', cancelledCount);
      return cancelledCount;
    }

    return previousCancelledCount;
  }

  /**
   * A list of company teams that the current user has permission to access
   * @returns {Array<CompanyTeam>}
   */
  @computed(
    'model.companyTeams.[]',
    'model.companyMemberships.[]',
    'session.currentUser.id',
  )
  get _currentUserCompanyTeams() {
    const currentUserId = this.session.currentUser.id;
    const currentUserIsTeamAdmin = this.model.companyMemberships.find(({ user }) => user.id === currentUserId)?.admin;
    const { companyTeams } = this.model;
    return companyTeams.filter(({ companyTeamMemberships }) => {
      const teamIncludesCurrentUser = companyTeamMemberships.map(({ companyMembership }) => companyMembership.user.id).includes(currentUserId);
      return teamIncludesCurrentUser || currentUserIsTeamAdmin;
    });
  }

  /**
   * These are the previous counts for the navigation.  Essentially, we'll always have a previous count
   * so the navigation won't change as we click through the different menus.
   *
   * @type {Number}
   * @private
   */
  _previousInProgressCount = 0;
  _previousCompletedCount = 0;
  _previousCancelledCount = 0;

  /**
   * Keep track of previous read states to show in between requests.
   *
   * @type {Boolean}
   */
  _previousInProgressIsRead = false;
  _previousCompletedIsRead = false;
  _previousCancelledIsRead = false;

  /**
   * @type {Boolean}
   * @private
   */
  @computed('state.applicationDashboardList.inProgressIsRead')
  get _inProgressIsRead() {
    return this._checkIsReadWithPrevious('inProgressIsRead', '_previousInProgressIsRead');
  }

  /**
   * @type {Boolean}
   * @private
   */
  @computed('state.applicationDashboardList.completedIsRead')
  get _completedIsRead() {
    return this._checkIsReadWithPrevious('completedIsRead', '_previousCompletedIsRead');
  }

  /**
   * @type {Boolean}
   * @private
   */
  @computed('state.applicationDashboardList.cancelledIsRead')
  get _cancelledIsRead() {
    return this._checkIsReadWithPrevious('cancelledIsRead', '_previousCancelledIsRead');
  }

  /**
   * Given the prop and previousProp name check the relevant isRead status to send back.
   *
   * @param {String} propName
   * @param {String} previousPropName
   * @private
   */
  _checkIsReadWithPrevious(propName, previousPropName) {
    const isRead = this.state.applicationDashboardList?.[propName];
    if (isRead == null) {
      return this[previousPropName];
    }

    this.set(previousPropName, isRead);
    return isRead;
  }

  /**
   * True when we have a queued search to run.
   *
   * @type {Boolean}
   * @private
   */
  _hasQueuedSearch = false;

  /**
   * True if we want to disable the filter sort component.
   *
   * @type {Boolean}
   */
  @readOnly('state.isLoadingResult') _isFilterSortDisabled;

  /**
   * Will be true when we want to show the filter/sort bar.
   *
   * @type {Boolean}
   */
  @computed(
    'state.isLoadingResult',
    'state.isSearchActivated',
    '_applicationListItems.[]',
    'state.isFirstQuery',
  )
  get _isShowFilterSort() {
    if (this.state.isLoadingResult || !this._applicationListItems?.length) {
      //we're loading a result, so show this if we have searched before.
      return !this.state.isFirstQuery;
    }

    return this.state.isSearchActivated || this._applicationListItems?.length;
  }

  /**
   * True when we want to show paging for our results
   *
   * @type {Boolean}
   * @private
   */
  @computed('state.isLoadingResult', '_isSearchActivated', 'state.applicationListItems.[]')
  get _isShowPaging() {
    return !this.state.isLoadingResult && !this._isSearchActivated && this.state.applicationListItems;
  }

  /**
   * True if we want to show loader
   *
   * @type {Boolean}
   * @private
   */
  @computed(
    '_isSearchActivated',
    'state.isLoadingResult',
    'state.applicationListItems.[]',
  )
  get _isShowSearchLoader() {
    if (!this._isSearchActivated) {
      return this.state.isLoadingResult;
    }

    return this.state.isLoadingResult && !this.state.applicationListItems?.length;
  }

  /**
   * Load the applications for our view.
   *
   * @param {String=} opt_secondarySort   of type ICON_SORTS.* set this secondary sort or if not set, just use whatever
   *                                      is currently in our state.
   * @private
   */
  _loadApplications(opt_secondarySort) {
    if (this.state.isResettingState || this.state.isLoading) {
      return;
    }

    const status = this.state.currentApplicationViewStatus;
    const sort = this._sortSelectedValue;
    const filter = this._networkFilterSelectedValue;
    const companyTeamFilterValue = this._companyTeamFilterSelectedValue;
    const secondarySort = opt_secondarySort
      ? opt_secondarySort
      : this._secondarySort;

    this.stateManager.resetStateManager();
    this.stateManager.setNotFirstQuery();

    this.stateManager.loadApplicationDashboardList(
      status,
      null,
      null,
      sort,
      filter,
      companyTeamFilterValue,
      secondarySort,
    );
  }

  /**
   * Array of objects that we'll feed into our lsg navigation component in the format:
   *
   * [{
   *  title: 'In Progress',
   *  count: 3,
   *  isActive: Function,
   *  isHighlight: Function,
   *  isHide: Function
   * },...]
   *
   * @type {Array}
   * @private
   */
  @computed(
    'state.currentApplicationViewStatus',
    '_inProgressCount',
    '_inProgressIsRead',
    '_completedCount',
    '_completedIsRead',
    '_cancelledCount',
    '_cancelledIsRead',
    '_isSearchActivated',
  )
  get _navItems() {
    const navItems = [];

    /**
     * @param {String} applicationStatus
     * @return {boolean}    true if the current status matches
     */
    const isStatus = (applicationStatus) => this.state.currentApplicationViewStatus === applicationStatus;

    navItems.push({
      title: PROJECTS_NAV_TITLES.IN_PROGRESS,
      count: this._inProgressCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.IN_PROGRESS_BUYER),
      isHighlight: () => !this._inProgressIsRead,
    });

    navItems.push({
      title: PROJECTS_NAV_TITLES.COMPLETE,
      count: this._completedCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.COMPLETE),
      isHighlight: () => !this._completedIsRead,
    });

    navItems.push({
      title: PROJECTS_NAV_TITLES.CANCELLED,
      count: this._cancelledCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.CANCELLED_BUYER),
      isHighlight: () => !this._cancelledIsRead,
    });

    return navItems;
  }

  /**
   * The options for the network filter select in the lsg-select format.
   *
   * [{
   *  title: 'Posted by me',
   *  value: 'me',
   *  selected: true/false
   * }...]
   *
   * @type {Array}
   * @private
   */
  @computed('_networkFilterSelectedValue')
  get _networkFilterOptions() {
    const filterOptions = [];

    filterOptions.push({
      title: PROJECTS_NETWORK_FILTER_OPTIONS.POSTED_BY_ANYONE,
      value: PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_ANYONE,
      selected: this._networkFilterSelectedValue === PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_ANYONE
    });

    filterOptions.push({
      title: PROJECTS_NETWORK_FILTER_OPTIONS.POSTED_BY_YOU,
      value: PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_YOU,
      selected: this._networkFilterSelectedValue === PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_YOU
    });

    filterOptions.push({
      title: PROJECTS_NETWORK_FILTER_OPTIONS.POSTED_BY_OTHERS,
      value: PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_OTHERS,
      selected: this._networkFilterSelectedValue === PROJECTS_NETWORK_FILTER_API_ENUMS.POSTED_BY_OTHERS
    });

    return filterOptions;
  }

  /**
   * The options for the team filter select in the lsg-select format.
   *
   * [{
   *  title: 'Team Name',
   *  value: '123',
   *  selected: true/false
   * }...]
   *
   * @type {Array}
   * @private
   */
  @computed('_currentUserCompanyTeams.[]', '_companyTeamFilterSelectedValue')
  get _companyTeamFilterOptions() {
    const filterOptions = [];

    filterOptions.push({
      title: PROJECTS_COMPANY_TEAM_FILTER_OPTIONS.ALL_TEAMS,
      value: null,
      selected: this._companyTeamFilterSelectedValue === null,
    });

    return this._currentUserCompanyTeams.reduce((accum, companyTeam) => {
      accum.push({
        title: companyTeam.name,
        value: companyTeam.id,
        selected: this._companyTeamFilterSelectedValue === companyTeam.id,
      });

      return accum;
    }, filterOptions);
  }

  @computed('_companyTeamFilterOptions.[]')
  get _showCompanyTeamFilter() {
    return this._companyTeamFilterOptions.length > 1;
  }

  /**
   * The selected value of the filter dropdown.
   *
   * @type {String}   of type PROJECTS_NETWORK_FILTER_API_ENUMS.*
   * @private
   */
  @alias('state.networkFilterSelectedValue') _networkFilterSelectedValue;

  /**
   * The selected value of the filter dropdown.
   *
   * @type {Number|null}   of type CompanyTeam.id
   * @private
   */
  @alias('state.companyTeamFilterSelectedValue') _companyTeamFilterSelectedValue;

  /**
   * Observe both filters and update based on the changes
   *
   * @private
   * @type {Function}
   */
  @action
  _observeFilter() {
    this._loadApplications(ICON_SORTS.SORT_DESCENDING);
  }

  /**
   * Watch the user's input in the search box, and update the search results from the server accordingly.
   */
  @action
  _observeSearchInput() {
    if (this.state.isResettingState) {
      return;
    }

    if (!this._isSearchActivated) {
      //search has been deactivated, so we'll swap our cache back to active.
      this.stateManager.swapCacheToDashboardList();
      this.set('_searchTextValue', '');
      return;
    }

    if (!this.state.applicationDashboardListWhileSearchingCache) {
      //we have results in our dashboard list, but none in our cache, so let's move these over.
      this.stateManager.swapDashboardListToCache();
    }

    const searchValue = this._searchTextValue;
    if (searchValue.length <= PROJECTS_SEARCH_CHARACTERS_BEFORE_REQUEST) {
      return;
    }

    debounce(this, this._runSearch, 500);
  }

  /**
   * Observe the secondary sort.
   */
  @action
  _observeSecondarySort() {
    this._loadApplications();
  }

  /**
   * The current pagination model
   *
   * @type {Pagination}
   * @private
   */
  @readOnly('state.currentPagination') _pagination;

  /**
   * Run a search for the current state's search text value
   *
   * @private
   */
  _runSearch() {
    if (this.state.isLoadingResult) {
      //we're already loading a result, so let's save this value, we only care about the latest value.
      this.set('_hasQueuedSearch', true);
      return;
    }

    const searchTextValue = this._searchTextValue;
    if (!searchTextValue) {
      this.set('_hasQueuedSearch', false);
      return;
    }

    this.stateManager.searchApplicationDashboardListForText(searchTextValue)
      .finally(() => {
        if (!this._hasQueuedSearch) {
          return;
        }

        this.set('_hasQueuedSearch', false);
        next(this, this._runSearch);
      });
  }

  /**
   * The search text value the user has typed in.
   *
   * @type {String}
   * @private
   */
  _searchTextValue = '';

  /**
   * The options for the sort select in the lsg-select format.
   *
   * [{
   *  title: 'Last Activity',
   *  value: 'last-activity',
   *  selected: true/false
   * }...]
   *
   * @type {Array}
   * @private
   */
  @computed('_sortSelectedValue')
  get _sortOptions() {
    const sortOptions = [];
    const sortSelectedValue = this._sortSelectedValue;

    sortOptions.push({
      title: PROJECTS_SORT_OPTIONS.LAST_ACTIVITY,
      value: PROJECTS_SORT_API_ENUMS.LAST_ACTIVITY,
      selected: sortSelectedValue === PROJECTS_SORT_API_ENUMS.LAST_ACTIVITY
    });

    sortOptions.push({
      title: PROJECTS_SORT_OPTIONS.DATE_STARTED,
      value: PROJECTS_SORT_API_ENUMS.DATE_STARTED,
      selected: sortSelectedValue === PROJECTS_SORT_API_ENUMS.DATE_STARTED
    });

    sortOptions.push({
      title: PROJECTS_SORT_OPTIONS.UNREAD_MESSAGES,
      value: PROJECTS_SORT_API_ENUMS.UNREAD_MESSAGES,
      selected: sortSelectedValue === PROJECTS_SORT_API_ENUMS.UNREAD_MESSAGES
    });

    sortOptions.push({
      title: PROJECTS_SORT_OPTIONS.POSTING_NAME,
      value: PROJECTS_SORT_API_ENUMS.POSTING_NAME,
      selected: sortSelectedValue === PROJECTS_SORT_API_ENUMS.POSTING_NAME
    });

    return sortOptions;
  }

  /**
   * The sort direction of the postings
   *
   * @type {String}   of type ICON_SORTS.*
   * @private
   */
  @alias('state.secondarySort') _secondarySort;

  /**
   * The selected value of the sort dropdown.
   *
   * @type {String}   of type PROJECTS_SORT_API_ENUMS.*
   * @private
   */
  _sortSelectedValue = PROJECTS_SORT_API_ENUMS.LAST_ACTIVITY;

  /**
   * True when the search component is activated
   *
   * NOTE: - This is an alias with the state manager, so you should NOT set this with a this.set()
   *
   * @type {Boolean}
   * @private
   */
  @alias('state.isSearchActivated') _isSearchActivated;

  /**
   * The user has clicked to advance backward in the search results list
   *
   * @private
   */
  @action
  _advancePageBackwardClicked() {
    this.stateManager.decrementPage(
      this._sortSelectedValue,
      this._networkFilterSelectedValue,
      this._companyTeamFilterSelectedValue,
      this._secondarySort,
    );
  }

  /**
   * The user has clicked to advance forward in the search results list
   *
   * @private
   */
  @action
  _advancePageForwardClicked() {
    this.stateManager.incrementPage(
      this._sortSelectedValue,
      this._networkFilterSelectedValue,
      this._companyTeamFilterSelectedValue,
      this._secondarySort,
    );
  }

  /**
   * Called when one of the navItems is clicked on
   *
   * @param {String} title    from one of the navItems
   * @private
   */
  @action
  _onNavItemClicked(title) {
    const newRoute = PROJECTS_NAV_TITLES_TO_ROUTES_MAP[title];
    if (this.router.currentPath === newRoute) {
      const currentStatus = this.state.currentApplicationViewStatus;

      this.resetController();
      this.stateManager.loadApplicationDashboardList(currentStatus);
      return;
    }

    this.router.transitionTo(newRoute);
  }
}

export default ProjectsController;
